import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import CTASection from "@/components/public/CTASection";
import { Search, ArrowRight, BookOpen, Filter } from "lucide-react";
import SeoHead from "@/components/public/SeoHead";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANGLE_LABELS = { educatif: "Éducatif", comparatif: "Comparatif", warning: "Mise en garde", coût: "Coût" };
const ANGLE_COLORS = { educatif: "bg-blue-100 text-blue-700", comparatif: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", coût: "bg-purple-100 text-purple-700" };

export default function BlogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [services, setServices] = useState([]);
  const [villes, setVilles] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentPage = parseInt(searchParams.get("page") || "1");
  const filterService = searchParams.get("service") || "";
  const filterVille = searchParams.get("ville") || "";
  const filterType = searchParams.get("type") || "";
  const filterAngle = searchParams.get("angle") || "";
  const filterSearch = searchParams.get("q") || "";

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", currentPage);
      params.set("limit", "12");
      if (filterService) params.set("service", filterService);
      if (filterVille) params.set("ville", filterVille);
      if (filterType) params.set("type_client", filterType);
      if (filterAngle) params.set("angle", filterAngle);
      if (filterSearch) params.set("search", filterSearch);

      const res = await axios.get(`${API}/blog?${params.toString()}`);
      setArticles(res.data.articles);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterService, filterVille, filterType, filterAngle, filterSearch]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/services`), axios.get(`${API}/villes`)]).then(([s, v]) => {
      setServices(s.data);
      setVilles(v.data);
    });
  }, []);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const setFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    p.set("page", "1");
    setSearchParams(p);
  };

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(p));
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="blog-list-page">
      <SeoHead
        title="Blog – Conseils nettoyage professionnel"
        description="Guides et conseils d'experts du nettoyage professionnel en Suisse romande. Astuces canapé, terrasse, moquette. Par Nettoyage Expert."
        path="/blog"
        breadcrumbs={[{ name: "Accueil", url: "/" }, { name: "Blog" }]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "Blog" }]} />
      {/* Hero */}
      <section className="bg-[#336DBF] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">
            Blog — Conseils et expertise en nettoyage professionnel
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">
            Guides, astuces et retours d'expérience par nos experts en Suisse romande
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-[#062133]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={16} className="text-[#062133]/60" />
            <span className="text-sm font-medium text-[#062133]/60">Filtrer les articles</span>
          </div>
          <div className="flex flex-wrap gap-3" data-testid="blog-filters">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#062133]/60" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filterSearch}
                onChange={e => setFilter("q", e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-[#062133]/15 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#336DBF]/30"
                data-testid="blog-search"
              />
            </div>
            <select
              value={filterService}
              onChange={e => setFilter("service", e.target.value)}
              className="border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm min-w-[160px]"
              data-testid="blog-filter-service"
            >
              <option value="">Tous les services</option>
              {services.map(s => <option key={s.slug} value={s.name}>{s.name}</option>)}
            </select>
            <select
              value={filterVille}
              onChange={e => setFilter("ville", e.target.value)}
              className="border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm min-w-[140px]"
              data-testid="blog-filter-ville"
            >
              <option value="">Toutes les villes</option>
              {villes.map(v => <option key={v.slug} value={v.name}>{v.name}</option>)}
            </select>
            <select
              value={filterType}
              onChange={e => setFilter("type", e.target.value)}
              className="border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm"
              data-testid="blog-filter-type"
            >
              <option value="">Tous les types</option>
              <option value="particulier">Particulier</option>
              <option value="entreprise">Entreprise</option>
            </select>
            <select
              value={filterAngle}
              onChange={e => setFilter("angle", e.target.value)}
              className="border border-[#062133]/15 rounded-md px-3 py-2.5 text-sm"
              data-testid="blog-filter-angle"
            >
              <option value="">Tous les angles</option>
              <option value="educatif">Éducatif</option>
              <option value="comparatif">Comparatif</option>
              <option value="warning">Mise en garde</option>
              <option value="coût">Coût</option>
            </select>
          </div>
          <p className="mt-3 text-xs text-[#062133]/60">{total} article{total > 1 ? "s" : ""} trouve{total > 1 ? "s" : ""}</p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12 sm:py-16 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white border border-[#062133]/10 rounded-lg p-5 animate-pulse">
                  <div className="flex gap-1.5 mb-3">
                    <div className="h-5 w-16 bg-gray-200 rounded" />
                    <div className="h-5 w-14 bg-gray-200 rounded" />
                    <div className="h-5 w-12 bg-gray-200 rounded" />
                  </div>
                  <div className="h-5 w-full bg-gray-200 rounded mb-2" />
                  <div className="h-5 w-3/4 bg-gray-200 rounded mb-4" />
                  <div className="h-4 w-full bg-gray-100 rounded mb-1" />
                  <div className="h-4 w-2/3 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <p className="text-center text-[#062133]/60 py-16">Aucun article trouve.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a, i) => (
                <FadeIn key={a.slug} delay={i * 50}>
                  <Link
                    to={`/blog/${a.slug}`}
                    className="group block bg-white border border-[#062133]/10 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all h-full"
                    data-testid={`blog-card-${a.slug}`}
                  >
                    <div className="p-5 flex flex-col h-full">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        <span className="text-xs font-medium bg-[#336DBF]/10 text-[#336DBF] px-2 py-0.5 rounded">{a.service_name?.replace("Nettoyage ", "Nett. ")}</span>
                        <span className="text-xs font-medium bg-[#F4F6F8] text-[#062133]/60 px-2 py-0.5 rounded">{a.ville_name}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${ANGLE_COLORS[a.angle] || "bg-gray-100 text-gray-600"}`}>
                          {ANGLE_LABELS[a.angle] || a.angle}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#062133] group-hover:text-[#336DBF] transition-colors text-base leading-snug mb-2">
                        {a.title}
                      </h3>
                      <p className="text-sm text-[#062133]/60 line-clamp-3 flex-1">
                        {a.copywriting?.intro?.substring(0, 150)}...
                      </p>
                      <div className="mt-4 pt-3 border-t border-[#062133]/5 flex items-center justify-between">
                        <span className="text-xs text-[#062133]/60">
                          {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" }) : ""}
                        </span>
                        <span className="text-[#336DBF] text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          Lire <ArrowRight size={12} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </FadeIn>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="mt-10 flex justify-center gap-2" data-testid="blog-pagination">
              {currentPage > 1 && (
                <button onClick={() => goToPage(currentPage - 1)} className="px-4 py-2 border border-[#062133]/15 rounded-md text-sm text-[#062133]/60 hover:bg-white transition-colors">
                  Precedent
                </button>
              )}
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                let p;
                if (pages <= 7) p = i + 1;
                else if (currentPage <= 4) p = i + 1;
                else if (currentPage >= pages - 3) p = pages - 6 + i;
                else p = currentPage - 3 + i;
                return (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    className={`w-10 h-10 rounded-md text-sm font-medium transition-colors ${p === currentPage ? "bg-[#336DBF] text-white" : "border border-[#062133]/15 text-[#062133]/60 hover:bg-white"}`}
                    data-testid={`page-${p}`}
                  >
                    {p}
                  </button>
                );
              })}
              {currentPage < pages && (
                <button onClick={() => goToPage(currentPage + 1)} className="px-4 py-2 border border-[#062133]/15 rounded-md text-sm text-[#062133]/60 hover:bg-white transition-colors">
                  Suivant
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <CTASection title="Besoin d'un devis gratuit ?" subtitle="Nos experts vous repondent sous 2 heures" />
    </div>
  );
}
